// Konfigurace lokálních SEO landing pages pro města
// Tyto stránky jsou "skryté" - nejsou v hlavní navigaci,
// ale jsou indexovány vyhledávači pro long-tail keywords

export interface CityFAQ {
  question: string;
  answer: string;
}

export interface CityLandingConfig {
  slug: string;
  urlPath: string;
  name: string;
  nameLocative: string; // "v Praze", "v Mostě" - pro české věty
  region: string;
  regionShort: string;
  isPrimary: boolean; // true = Ústecký kraj, false = Praha/Středočeský
  distanceFromTeplice: number; // km
  travelTime: string; // "do 30 minut"
  nearbyCities: string[];
  coordinates: { lat: number; lng: number };
  
  // SEO
  metaTitle: string;
  metaDescription: string;
  
  // Content
  heroTitle: string;
  heroSubtitle: string;
  heroDescription: string;
  
  // USP specifické pro město
  benefits: {
    icon: string;
    title: string;
    description: string;
  }[];
  
  // FAQ specifické pro město
  faq: CityFAQ[];
}

export const CITY_LANDING_PAGES: Record<string, CityLandingConfig> = {
  "praha": {
    slug: "praha",
    urlPath: "/elektrikar-praha",
    name: "Praha",
    nameLocative: "v Praze",
    region: "Středočeský kraj",
    regionShort: "Středočeský",
    isPrimary: false,
    distanceFromTeplice: 100,
    travelTime: "do 90 minut",
    nearbyCities: ["Kladno", "Mělník", "Neratovice", "Brandýs nad Labem", "Říčany"],
    coordinates: { lat: 50.0755, lng: 14.4378 },
    
    metaTitle: "Elektrikář Praha - Profesionální elektroinstalace | ESKO Servis",
    metaDescription: "Hledáte spolehlivého elektrikáře v Praze? ESKO Servis nabízí elektroinstalace, revize a opravy. Výjezd pro větší projekty. ☎ +420 732 658 712",
    
    heroTitle: "Elektrikář Praha",
    heroSubtitle: "Profesionální elektrikářské služby v Praze a Středočeském kraji",
    heroDescription: "Specializujeme se na větší projekty v Praze a okolí. Elektroinstalace, revize, fotovoltaika a průmyslové rozvody s profesionálním přístupem.",
    
    benefits: [
      {
        icon: "Building2",
        title: "Větší projekty",
        description: "Zaměřujeme se na komerční a průmyslové projekty v hlavním městě"
      },
      {
        icon: "Shield",
        title: "Certifikované revize",
        description: "Oprávnění pro revize elektroinstalací dle ČSN 33 2000-6"
      },
      {
        icon: "Zap",
        title: "Fotovoltaika",
        description: "Kompletní instalace fotovoltaických systémů pro firmy"
      }
    ],
    
    faq: [
      {
        question: "Jak rychle přijedete do Prahy?",
        answer: "Pro větší projekty v Praze jsme schopni dorazit do 24 hodin od domluvy. Pro urgentní případy nabízíme expresní výjezd."
      },
      {
        question: "Jaké projekty v Praze realizujete?",
        answer: "V Praze se zaměřujeme především na větší komerční projekty, průmyslové elektroinstalace, revize a instalace fotovoltaiky."
      },
      {
        question: "Kolik stojí výjezd elektrikáře do Prahy?",
        answer: "Cena výjezdu závisí na rozsahu projektu. Pro větší zakázky je výjezd součástí celkové ceny. Kontaktujte nás pro nezávaznou kalkulaci."
      },
      {
        question: "Máte reference z Prahy?",
        answer: "Ano, máme realizace v Praze a okolí. Rádi vám poskytneme reference od spokojených zákazníků z hlavního města."
      }
    ]
  },
  
  "usti-nad-labem": {
    slug: "usti-nad-labem",
    urlPath: "/elektrikar-usti-nad-labem",
    name: "Ústí nad Labem",
    nameLocative: "v Ústí nad Labem",
    region: "Ústecký kraj",
    regionShort: "Ústecký",
    isPrimary: true,
    distanceFromTeplice: 15,
    travelTime: "do 20 minut",
    nearbyCities: ["Chabařovice", "Trmice", "Střekov", "Neštěmice", "Vaňov"],
    coordinates: { lat: 50.6607, lng: 14.0323 },
    
    metaTitle: "Elektrikář Ústí nad Labem - Rychlý výjezd | ESKO Servis",
    metaDescription: "Elektrikář Ústí nad Labem ⚡ Rychlý výjezd do 20 minut. Elektroinstalace, revize, opravy a havarijní služba. ☎ +420 732 658 712",
    
    heroTitle: "Elektrikář Ústí nad Labem",
    heroSubtitle: "Spolehlivý elektrikář pro Ústí nad Labem a okolí",
    heroDescription: "Jsme jen 15 km od Ústí nad Labem. Nabízíme rychlý výjezd, profesionální práci a férové ceny pro domácnosti i firmy.",
    
    benefits: [
      {
        icon: "Clock",
        title: "Rychlý výjezd",
        description: "Do Ústí nad Labem dorazíme do 20 minut od zavolání"
      },
      {
        icon: "Home",
        title: "Domácnosti i firmy",
        description: "Kompletní elektrikářské služby pro rodinné domy i kanceláře"
      },
      {
        icon: "Wrench",
        title: "Servis a opravy",
        description: "Opravy poruch, výměny jističů, zásuvek a vypínačů"
      }
    ],
    
    faq: [
      {
        question: "Jak rychle přijedete do Ústí nad Labem?",
        answer: "Do Ústí nad Labem dorazíme obvykle do 20 minut. V případě havarijní pohotovosti vyjíždíme okamžitě."
      },
      {
        question: "Děláte elektroinstalace v panelových domech v Ústí?",
        answer: "Ano, máme bohaté zkušenosti s rekonstrukcemi elektroinstalací v panelových domech po celém Ústí nad Labem."
      },
      {
        question: "Kolik stojí výjezd do Ústí nad Labem?",
        answer: "Výjezd do Ústí nad Labem je zdarma při realizaci zakázky. Pro diagnostiku účtujeme symbolický poplatek, který se odečítá z ceny práce."
      },
      {
        question: "Provádíte revize v Ústí nad Labem?",
        answer: "Ano, provádíme kompletní revize elektroinstalací, hromosvodů i elektrospotřebičů v celém Ústí nad Labem a okolí."
      }
    ]
  },
  
  "most": {
    slug: "most",
    urlPath: "/elektrikar-most",
    name: "Most",
    nameLocative: "v Mostě",
    region: "Ústecký kraj",
    regionShort: "Ústecký",
    isPrimary: true,
    distanceFromTeplice: 18,
    travelTime: "do 25 minut",
    nearbyCities: ["Litvínov", "Meziboří", "Lom", "Osek", "Hora Svaté Kateřiny"],
    coordinates: { lat: 50.5031, lng: 13.6367 },
    
    metaTitle: "Elektrikář Most - Elektroinstalace a revize | ESKO Servis",
    metaDescription: "Elektrikář Most ⚡ Profesionální elektroinstalace, revize a opravy. Výjezd do 25 minut. Působíme i v Litvínově. ☎ +420 732 658 712",
    
    heroTitle: "Elektrikář Most",
    heroSubtitle: "Elektrikářské služby pro Most, Litvínov a okolí",
    heroDescription: "Kompletní elektrikářské služby pro Most a přilehlé obce. Od drobných oprav po kompletní elektroinstalace novostaveb.",
    
    benefits: [
      {
        icon: "Clock",
        title: "Do 25 minut na místě",
        description: "Rychlý výjezd z Teplic do Mostu a okolních obcí"
      },
      {
        icon: "Factory",
        title: "Průmyslové objekty",
        description: "Zkušenosti s elektroinstalacemi v průmyslových halách"
      },
      {
        icon: "Sun",
        title: "Fotovoltaika",
        description: "Instalace solárních panelů pro rodinné domy i firmy"
      }
    ],
    
    faq: [
      {
        question: "Jak rychle přijedete do Mostu?",
        answer: "Do Mostu dorazíme obvykle do 25 minut. Jsme vzdáleni pouze 18 km od centra města."
      },
      {
        question: "Děláte elektroinstalace i v Litvínově?",
        answer: "Ano, působíme v celém regionu Most-Litvínov včetně okolních obcí jako Meziboří, Lom nebo Osek."
      },
      {
        question: "Instalujete fotovoltaiku v Mostě?",
        answer: "Ano, nabízíme kompletní instalace fotovoltaických elektráren pro rodinné domy i firemní objekty v Mostě a okolí."
      },
      {
        question: "Máte zkušenosti s průmyslovými objekty?",
        answer: "Ano, máme bohaté zkušenosti s elektroinstalacemi v průmyslových halách a výrobních objektech v mosteckém regionu."
      }
    ]
  },
  
  "decin": {
    slug: "decin",
    urlPath: "/elektrikar-decin",
    name: "Děčín",
    nameLocative: "v Děčíně",
    region: "Ústecký kraj",
    regionShort: "Ústecký",
    isPrimary: true,
    distanceFromTeplice: 40,
    travelTime: "do 45 minut",
    nearbyCities: ["Benešov nad Ploučnicí", "Česká Kamenice", "Jílové", "Huntířov"],
    coordinates: { lat: 50.7814, lng: 14.2148 },
    
    metaTitle: "Elektrikář Děčín - Spolehlivé elektroinstalace | ESKO Servis",
    metaDescription: "Elektrikář Děčín ⚡ Elektroinstalace, revize a opravy elektriky. Působíme v Děčíně a okolí. ☎ +420 732 658 712",
    
    heroTitle: "Elektrikář Děčín",
    heroSubtitle: "Profesionální elektrikář pro Děčín a Děčínsko",
    heroDescription: "Nabízíme kompletní elektrikářské služby pro Děčín a okolní obce. Kvalitní práce, spolehlivost a férové ceny.",
    
    benefits: [
      {
        icon: "MapPin",
        title: "Celé Děčínsko",
        description: "Působíme v Děčíně i okolních obcích včetně Benešova nad Ploučnicí"
      },
      {
        icon: "Home",
        title: "Rodinné domy",
        description: "Elektroinstalace novostaveb i rekonstrukce starších domů"
      },
      {
        icon: "FileCheck",
        title: "Revize s certifikátem",
        description: "Revizní zprávy pro pojišťovny, kolaudace a pravidelné kontroly"
      }
    ],
    
    faq: [
      {
        question: "Jak rychle přijedete do Děčína?",
        answer: "Do Děčína dorazíme obvykle do 45 minut. Pro plánované práce se vždy domlouváme na konkrétním termínu."
      },
      {
        question: "Působíte i v České Kamenici?",
        answer: "Ano, působíme v celém okresu Děčín včetně České Kamenice, Benešova nad Ploučnicí a dalších obcí."
      },
      {
        question: "Děláte revize pro kolaudaci?",
        answer: "Ano, provádíme kompletní revize elektroinstalací potřebné pro kolaudaci novostaveb i rekonstrukcí."
      },
      {
        question: "Máte zkušenosti s chatami a rekreačními objekty?",
        answer: "Ano, v Děčínsku realizujeme elektroinstalace i revize rekreačních objektů, chat a chalup."
      }
    ]
  },
  
  "chomutov": {
    slug: "chomutov",
    urlPath: "/elektrikar-chomutov",
    name: "Chomutov",
    nameLocative: "v Chomutově",
    region: "Ústecký kraj",
    regionShort: "Ústecký",
    isPrimary: true,
    distanceFromTeplice: 35,
    travelTime: "do 40 minut",
    nearbyCities: ["Jirkov", "Kadaň", "Klášterec nad Ohří", "Údlice"],
    coordinates: { lat: 50.4603, lng: 13.4180 },
    
    metaTitle: "Elektrikář Chomutov - Elektroinstalace a opravy | ESKO Servis",
    metaDescription: "Elektrikář Chomutov ⚡ Profesionální elektroinstalace, revize a servis. Působíme i v Jirkově a Kadani. ☎ +420 732 658 712",
    
    heroTitle: "Elektrikář Chomutov",
    heroSubtitle: "Elektrikářské služby pro Chomutov, Jirkov a okolí",
    heroDescription: "Kompletní elektrikářské práce pro Chomutov a okolí. Od malých oprav po velké projekty s profesionálním přístupem.",
    
    benefits: [
      {
        icon: "Clock",
        title: "Do 40 minut na místě",
        description: "Rychlý výjezd do Chomutova, Jirkova i Kadaně"
      },
      {
        icon: "Zap",
        title: "Elektroinstalace",
        description: "Novostavby, rekonstrukce i rozšíření stávajících rozvodů"
      },
      {
        icon: "Shield",
        title: "Zabezpečení",
        description: "Instalace alarmů a kamerových systémů"
      }
    ],
    
    faq: [
      {
        question: "Jak rychle přijedete do Chomutova?",
        answer: "Do Chomutova dorazíme obvykle do 40 minut. Pro plánované práce se domlouváme na konkrétním termínu."
      },
      {
        question: "Působíte i v Kadani a Klášterci?",
        answer: "Ano, působíme v celém chomutovském regionu včetně Kadaně, Klášterce nad Ohří a okolních obcí."
      },
      {
        question: "Instalujete zabezpečovací systémy?",
        answer: "Ano, nabízíme instalace alarmů, kamerových systémů a kompletních zabezpečovacích řešení pro domy i firmy."
      },
      {
        question: "Kolik stojí elektroinstalace rodinného domu?",
        answer: "Cena závisí na velikosti domu a rozsahu prací. Kontaktujte nás pro nezávaznou kalkulaci dle vašich požadavků."
      }
    ]
  },
  
  "bilina": {
    slug: "bilina",
    urlPath: "/elektrikar-bilina",
    name: "Bílina",
    nameLocative: "v Bílině",
    region: "Ústecký kraj",
    regionShort: "Ústecký",
    isPrimary: true,
    distanceFromTeplice: 10,
    travelTime: "do 15 minut",
    nearbyCities: ["Duchcov", "Hostomice", "Kostomlaty", "Ledvice"],
    coordinates: { lat: 50.5481, lng: 13.7756 },
    
    metaTitle: "Elektrikář Bílina - Rychlý výjezd do 15 minut | ESKO Servis",
    metaDescription: "Elektrikář Bílina ⚡ Nejrychlejší výjezd v regionu - do 15 minut. Elektroinstalace, opravy, revize. ☎ +420 732 658 712",
    
    heroTitle: "Elektrikář Bílina",
    heroSubtitle: "Váš nejbližší elektrikář pro Bílinu a Duchcov",
    heroDescription: "Jsme nejblíže! Z Teplic jsme v Bílině za 15 minut. Rychlá pomoc při poruchách i profesionální elektroinstalace.",
    
    benefits: [
      {
        icon: "Zap",
        title: "Nejrychlejší výjezd",
        description: "Do Bíliny dorazíme za pouhých 15 minut"
      },
      {
        icon: "Wrench",
        title: "Opravy a servis",
        description: "Rychlé opravy poruch, výměny a údržba"
      },
      {
        icon: "Home",
        title: "Domácí elektrika",
        description: "Vše pro vaši domácnost - od zásuvek po rozvaděče"
      }
    ],
    
    faq: [
      {
        question: "Jak rychle přijedete do Bíliny?",
        answer: "Do Bíliny dorazíme nejrychleji ze všech měst v okolí - obvykle za 15 minut od zavolání."
      },
      {
        question: "Působíte i v Duchcově?",
        answer: "Ano, Duchcov je hned vedle a patří do naší primární oblasti. Výjezd je stejně rychlý jako do Bíliny."
      },
      {
        question: "Řešíte i malé opravy?",
        answer: "Samozřejmě! Řešíme vše od výměny zásuvky po kompletní elektroinstalace. Žádná práce není příliš malá."
      },
      {
        question: "Máte pohotovostní službu?",
        answer: "Ano, nabízíme havarijní pohotovost. V případě nouze volejte na +420 732 658 712."
      }
    ]
  },
  
  "litomerice": {
    slug: "litomerice",
    urlPath: "/elektrikar-litomerice",
    name: "Litoměřice",
    nameLocative: "v Litoměřicích",
    region: "Ústecký kraj",
    regionShort: "Ústecký",
    isPrimary: true,
    distanceFromTeplice: 25,
    travelTime: "do 30 minut",
    nearbyCities: ["Lovosice", "Roudnice nad Labem", "Terezín", "Třebívlice"],
    coordinates: { lat: 50.5338, lng: 14.1318 },
    
    metaTitle: "Elektrikář Litoměřice - Elektroinstalace a revize | ESKO Servis",
    metaDescription: "Elektrikář Litoměřice ⚡ Profesionální elektroinstalace, revize a opravy. Působíme i v Lovosicích. ☎ +420 732 658 712",
    
    heroTitle: "Elektrikář Litoměřice",
    heroSubtitle: "Elektrikářské služby pro Litoměřice a Podřipsko",
    heroDescription: "Kvalitní elektrikářské práce pro Litoměřice, Lovosice a okolí. Spolehlivost, profesionalita a férové ceny.",
    
    benefits: [
      {
        icon: "Clock",
        title: "Do 30 minut na místě",
        description: "Rychlý výjezd do Litoměřic a okolních obcí"
      },
      {
        icon: "Building2",
        title: "Historické objekty",
        description: "Zkušenosti s elektroinstalacemi ve starších budovách"
      },
      {
        icon: "FileCheck",
        title: "Revize pro památky",
        description: "Revize elektroinstalací pro památkově chráněné objekty"
      }
    ],
    
    faq: [
      {
        question: "Jak rychle přijedete do Litoměřic?",
        answer: "Do Litoměřic dorazíme obvykle do 30 minut. Pro plánované práce se domlouváme na konkrétním čase."
      },
      {
        question: "Působíte i v Lovosicích a Roudnici?",
        answer: "Ano, působíme v celém litoměřickém regionu včetně Lovosic, Roudnice nad Labem a okolních obcí."
      },
      {
        question: "Máte zkušenosti se staršími budovami?",
        answer: "Ano, máme bohaté zkušenosti s rekonstrukcemi elektroinstalací ve starších a historických budovách v Litoměřicích."
      },
      {
        question: "Děláte revize pro pojišťovny?",
        answer: "Ano, provádíme revize elektroinstalací s certifikátem pro pojišťovny, kolaudace i pravidelné kontroly."
      }
    ]
  }
};

// Helper funkce pro získání konfigurace města podle URL path
export const getCityByUrlPath = (path: string): CityLandingConfig | null => {
  const city = Object.values(CITY_LANDING_PAGES).find(c => c.urlPath === path);
  return city || null;
};

// Helper pro získání všech URL paths pro routing
export const getAllCityPaths = (): string[] => {
  return Object.values(CITY_LANDING_PAGES).map(c => c.urlPath);
};

// Helper pro generování JSON-LD pro město
export const getCityJsonLd = (city: CityLandingConfig, services: { title: string; slug: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://eskoservis.cz${city.urlPath}`,
    "name": `ESKO Servis - Elektrikář ${city.name}`,
    "description": city.heroDescription,
    "url": `https://eskoservis.cz${city.urlPath}`,
    "telephone": "+420732658712",
    "email": "josefkotrba@post.cz",
    "priceRange": "$$",
    "image": "https://eskoservis.cz/og-image.jpg",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Teplice",
      "addressRegion": "Ústecký kraj",
      "addressCountry": "CZ"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 50.6404,
      "longitude": 13.8244
    },
    "areaServed": [
      {
        "@type": "City",
        "name": city.name,
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": city.coordinates.lat,
          "longitude": city.coordinates.lng
        }
      },
      ...city.nearbyCities.map(nearbyCity => ({
        "@type": "City",
        "name": nearbyCity
      })),
      {
        "@type": "AdministrativeArea",
        "name": city.region
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `Elektrikářské služby ${city.nameLocative}`,
      "itemListElement": services.map((service, index) => ({
        "@type": "Offer",
        "position": index + 1,
        "itemOffered": {
          "@type": "Service",
          "name": `${service.title} ${city.name}`,
          "url": `https://eskoservis.cz/sluzby/${service.slug}`
        }
      }))
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "12",
      "bestRating": "5",
      "worstRating": "1"
    }
  };
};

// FAQ Schema pro město
export const getCityFAQSchema = (city: CityLandingConfig) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": city.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
};
